import React from 'react';
import { X, Calendar, Download, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { Report } from './ReportCard';

interface ReportDetailModalProps {
    report: Report | null;
    onClose: () => void;
}

const ReportDetailModal: React.FC<ReportDetailModalProps> = ({ report, onClose }) => {

    // Close on escape key
    React.useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    // Parse stats if available
    const keyStats = React.useMemo(() => {
        if (!report || !report.key_stats_json) return null;
        try {
            return JSON.parse(report.key_stats_json);
        } catch (e) {
            console.error("Failed to parse key stats", e);
            return null;
        }
    }, [report]);

    if (!report) return null;

    const statsArray = keyStats ? [
        { label: 'Indice BRVM C', value: keyStats?.market_stats?.brvm_c_change, isPct: true, icon: Activity },
        { label: 'Volume Total', value: keyStats?.market_stats?.total_volume_fcfa, isCurrency: true, icon: Activity },
    ] : [];

    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
        }} onClick={onClose}>
            <div
                className="custom-scrollbar"
                style={{
                    background: 'var(--bg-box)',
                    borderRadius: '24px',
                    width: '100%',
                    maxWidth: '700px',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    position: 'relative',
                    animation: 'modalSlideIn 0.3s ease-out'
                }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{
                    padding: '24px',
                    borderBottom: '1px solid var(--border-color)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start'
                }}>
                    <div>
                        <div style={{
                            fontSize: '12px', fontWeight: '700', color: '#3b82f6',
                            textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px'
                        }}>
                            {report.report_type} REPORT
                        </div>
                        <h2 style={{ fontSize: '24px', fontWeight: '800', color: 'var(--text-heading)', lineHeight: '1.2' }}>
                            {report.title}
                        </h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', color: 'var(--text-muted)' }}>
                            <Calendar size={16} />
                            <span style={{ fontSize: '14px' }}>
                                Publié le {new Date(report.publication_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'var(--bg-input)', border: 'none', borderRadius: '50%',
                            width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: 'var(--text-muted)'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '24px' }}>

                    {/* Stats Banner if available */}
                    {keyStats && (
                        <div style={{
                            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px',
                            marginBottom: '24px'
                        }}>
                            {/* Cannot easily map dynamic stats without knowing structure, using generic placeholder if needed or specific fields */}
                        </div>
                    )}

                    <div style={{
                        fontSize: '16px', lineHeight: '1.7', color: 'var(--text-main)',
                        background: 'var(--bg-input)', padding: '24px', borderRadius: '16px',
                        border: '1px solid var(--border-color)'
                    }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '16px', color: 'var(--text-heading)' }}>
                            Synthèse
                        </h3>
                        <div
                            className="report-html-content"
                            dangerouslySetInnerHTML={{ __html: report.summary_html || "Aucune synthèse disponible." }}
                        />
                    </div>
                </div>

                {/* Footer / Actions */}
                <div style={{
                    padding: '20px 24px',
                    borderTop: '1px solid var(--border-color)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    background: 'var(--bg-box)',
                    borderBottomLeftRadius: '24px',
                    borderBottomRightRadius: '24px',
                    position: 'sticky', bottom: 0
                }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '12px 24px', borderRadius: '12px', border: '1px solid var(--border-color)',
                            background: 'var(--bg-input)', color: 'var(--text-main)', fontWeight: '600', cursor: 'pointer'
                        }}
                    >
                        Fermer
                    </button>
                </div>
            </div>
            <style>{`
                @keyframes modalSlideIn {
                    from { opacity: 0; transform: translateY(20px) scale(0.95); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: var(--border-color);
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: var(--text-muted);
                }
                .report-html-content {
                    color: var(--text-main);
                }
                .report-html-content p {
                    margin-bottom: 1em;
                }
                .report-html-content strong {
                    color: var(--text-heading);
                }
            `}</style>
        </div >
    );
};

export default ReportDetailModal;

